import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useClientRouteHost } from '../context/ClientRouteHostContext'
import { CircularProgress, Alert, Dialog, DialogContent, DialogActions } from '@mui/material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/en'
import 'dayjs/locale/sv'
import 'dayjs/locale/nb'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../pure-js/libs/RoutePath'
import styled from '@emotion/styled'
import { DropinPackage, Client, DropinTicketStatus, PaymentMethod } from '../../../pure-js/types/GrooverTypes'
import type { DropinTicketLog } from '../../../pure-js/types/GrooverTypes'
import { useDropinPackages } from '../hooks/useDropinPackages'
import { getClient } from '../libs/DBApiHandler'
import { isPaymentMethodEnabled } from '../../../pure-js/libs/PaymentHelper'
import { Elements } from '@stripe/react-stripe-js'
import getStripe from '../libs/getStripe'
import { getStripeConfig } from '../libs/DBApiHandler'
import StripeCheckoutForm from '../components/StripeCheckoutForm'
import { StripeConfig, StripePaymentIntentStatusValue } from '../../../pure-js/types/StripeTypes'
import {
  createDropinTicketAndInitiateSwishPayment,
  getDropinTicketActivationStatus,
  cancelSwishPaymentIntent,
  createStripePaymentIntentForDropinTicket
} from '../libs/CloudFunctionsApiHandler'
import { SwishPaymentStatus } from '../../../pure-js/types/SwishTypes'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { getInternationalPhoneNumber } from '../../../pure-js/libs/PhoneHelper'
import { useSwishPaymentIntentStatus, useStripePaymentIntentStatus } from '../libs/DBApiHandler'
import Box from '../components/Box'
import GrooverText from '../components/GrooverText'
import { getGrooverText, getGrooverTextStyle } from '../libs/GrooverTextRepository'
import { _getLanguageCode } from '../hooks/useLanguage'
import useDeviceDetect from '../hooks/useDeviceDetect'
import swishIcon from '/public/swishIcon.svg'
import swishBigLogo from '/public/swishBigLogo.svg'
import QRCodeElement from '../components/QRCodeElement'
import swedishAppStoreBadge from '/Download_on_the_App_Store_Badge_SE_RGB_blk_100317.svg'
import englishAppStoreBadge from '/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg'
import { Texts } from '../../../pure-js/libs/Texts'
import { getFunctionUrl } from '../libs/FunctionsUrlHelper'
import { CloudFunction } from '../../../pure-js/enums/CloudFunction'
import { AddToWalletButtons } from '../components/AddToWalletButtons'
import ReturningUserBanner from '../components/ReturningUserBanner'
import { StorageKey, ReturningUserProfile, getStoredReturningUserProfile } from '../hooks/useLocalStorageItemHelper'

dayjs.extend(relativeTime)

const isRecentlyActivated = (log: DropinTicketLog, withinMinutes = 60): boolean => {
  const logTime = new Date(log.timestamp).getTime()
  const now = Date.now()
  const diffMs = now - logTime
  const diffMinutes = diffMs / (1000 * 60)
  return diffMinutes >= 0 && diffMinutes <= withinMinutes
}

const formatMinutesAgo = (timestamp: string, locale: string): string => {
  const dayjsLocale = locale === 'no' ? 'nb' : locale
  return dayjs(timestamp).locale(dayjsLocale).fromNow()
}

interface PostModalTicketViewContentProps {
  postModalTicketView: 'activated' | 'qr'
  lastConsumeLog: DropinTicketLog | null
  ticketId: string
  clientId: string | undefined
  onReset: () => void
  onRefresh?: () => Promise<void>
  refreshLoading?: boolean
}

const PostModalTicketViewContent: React.FC<PostModalTicketViewContentProps> = ({
  postModalTicketView,
  lastConsumeLog,
  ticketId,
  clientId,
  onReset,
  onRefresh,
  refreshLoading = false
}) => {
  const appleWalletUrl =
    ticketId && clientId
      ? `${getFunctionUrl(CloudFunction.appleWalletDropinPass)}?ticketId=${encodeURIComponent(ticketId)}&clientId=${encodeURIComponent(clientId)}`
      : null
  const googleWalletUrl =
    ticketId && clientId
      ? `${getFunctionUrl(CloudFunction.googleWalletDropinPass)}?ticketId=${encodeURIComponent(ticketId)}&clientId=${encodeURIComponent(clientId)}`
      : null
  const locale = _getLanguageCode() || 'en'
  const CardWrapper =
    postModalTicketView === 'activated' && lastConsumeLog ? PostModalTicketCardActivated : PostModalTicketCard
  return (
    <CardWrapper>
      {postModalTicketView === 'activated' && lastConsumeLog ? (
        <>
          <PostModalActivatedSection>
            <PostModalActivatedText>
              {getGrooverText('portalDropinTicketLastActivatedPrefix', locale)}
              <span style={{ whiteSpace: 'nowrap' }}>{formatMinutesAgo(lastConsumeLog.timestamp, locale)}</span>
              {getGrooverText('portalDropinTicketLastActivatedSuffix', locale)}
            </PostModalActivatedText>
            <PostModalOkLabel>
              <GrooverText textKey="portalDropinTicketOkScreen" />
            </PostModalOkLabel>
          </PostModalActivatedSection>
          <QRCodeSection style={{ marginTop: 0 }}>
            <QRCodeContainer>
              <QRCodeElement value={`d/${ticketId}`} showText={false} />
            </QRCodeContainer>
            <AddToWalletButtons appleWalletUrl={appleWalletUrl} googleWalletUrl={googleWalletUrl} />
          </QRCodeSection>
        </>
      ) : (
        <>
          <QRCodeSection>
            <QRCodeTitle>
              <GrooverText textKey="checkoutPageYourTicketId" />
            </QRCodeTitle>
            <QRCodeContainer>
              <QRCodeElement value={`d/${ticketId}`} showText={false} />
            </QRCodeContainer>
            <TicketIdText>{ticketId}</TicketIdText>
            {onRefresh && (
              <RefreshButton onClick={onRefresh} disabled={refreshLoading}>
                {refreshLoading ? <CircularProgress size={20} style={{ marginRight: 8 }} /> : null}
                <GrooverText textKey="checkoutPageRefreshStatus" style={{ color: '#16a34a' }} />
              </RefreshButton>
            )}
            <AddToWalletButtons appleWalletUrl={appleWalletUrl} googleWalletUrl={googleWalletUrl} />
          </QRCodeSection>
        </>
      )}
      <ReturnButton onClick={onReset}>
        <GrooverText textKey="checkoutPageReturnToCheckout" />
      </ReturnButton>
    </CardWrapper>
  )
}

/**
 * CheckoutPage component for purchasing drop-in packages
 *
 * URL: /checkout/:clientId/:productType
 *
 * Supported productTypes:
 * - dropinPackages: Shows available drop-in packages for the client
 *
 * Example usage:
 * /checkout/abc123/dropinPackages
 */

interface UserInfo {
  name: string
  phoneNumber: string
  email: string
}

export const CheckoutPage: React.FC = () => {
  const { productType } = useParams<{ productType: string }>()
  const { clientId } = useClientRouteHost()
  const [isProcessing, setIsProcessing] = useState(false)
  const [swishInstructionUUID, setSwishInstructionUUID] = useState('')
  const [swishPaymentIntentStatus, setSwishPaymentIntentStatus] = useState<SwishPaymentStatus>(
    SwishPaymentStatus.PENDING
  )
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [selectedPackage, setSelectedPackage] = useState<DropinPackage | null>(null)
  const [client, setClient] = useState<Client | null>(null)
  const [userInfo, setUserInfo] = useState<UserInfo>({
    name: '',
    phoneNumber: '',
    email: ''
  })
  const [validationErrors, setValidationErrors] = useState<{
    email?: string
    phoneNumber?: string
  }>({})
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [paymentFailed, setPaymentFailed] = useState(false)
  const [paymentAbortInProgress, setPaymentAbortInProgress] = useState(false)
  const [ticketId, setTicketId] = useState<string | null>(null)
  const [postModalTicketView, setPostModalTicketView] = useState<'activated' | 'qr' | null>(null)
  const [lastConsumeLog, setLastConsumeLog] = useState<DropinTicketLog | null>(null)
  const [postModalLoading, setPostModalLoading] = useState(false)
  const [postModalRefreshLoading, setPostModalRefreshLoading] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<'swish' | 'stripe'>('swish')
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null)
  const [stripeConfig, setStripeConfig] = useState<StripeConfig | null>(null)
  const [stripeTicketId, setStripeTicketId] = useState<string | null>(null)
  /** Kept after clientSecret is cleared post-payment so Firestore can still listen to stripe_payment_intent_status/{pi_…} */
  const [stripePaymentIntentIdPersisted, setStripePaymentIntentIdPersisted] = useState<string | null>(null)

  // Extract paymentIntentId from clientSecret format: "pi_XXXXX_secret_YYYYY"
  const stripePaymentIntentIdFromSecret = useMemo(
    () => stripeClientSecret?.split('_secret_')[0] ?? null,
    [stripeClientSecret]
  )
  const stripePaymentIntentId = stripePaymentIntentIdFromSecret ?? stripePaymentIntentIdPersisted
  const stripePaymentIntentStatus = useStripePaymentIntentStatus(stripePaymentIntentId)

  /** First success path (Firestore PI doc vs activation poll vs 30s fallback) wins; avoids duplicate transitions. */
  const stripePostPaymentResolvedRef = useRef(false)
  const stripePostPaymentFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearStripePostPaymentFallbackTimer = useCallback(() => {
    if (stripePostPaymentFallbackTimerRef.current != null) {
      clearTimeout(stripePostPaymentFallbackTimerRef.current)
      stripePostPaymentFallbackTimerRef.current = null
    }
  }, [])

  const completeStripePostPaymentSuccess = useCallback(() => {
    if (stripePostPaymentResolvedRef.current) return
    stripePostPaymentResolvedRef.current = true
    clearStripePostPaymentFallbackTimer()
    setStripePaymentIntentIdPersisted(null)
    setPostModalLoading(false)
    setPostModalTicketView('qr')
  }, [clearStripePostPaymentFallbackTimer])

  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [returningProfile] = useState<ReturningUserProfile | null>(() => getStoredReturningUserProfile())
  const [isStudent, setIsStudent] = useState(false)

  const { packages, currency, loading, error } = useDropinPackages(clientId)
  const showAppDownloadSection = client?.featureToggles?.registrationAppDownload === true && currency === 'SEK'
  let swishPaymentIntentMonitor = useSwishPaymentIntentStatus(swishInstructionUUID)
  // Phone-only detection: deep linking into Swish app doesn't make sense on desktop browsers.
  const { isMobile: isPhoneDevice } = useDeviceDetect()

  // Auto-select popular package when packages are loaded
  useEffect(() => {
    if (packages && packages.length > 0 && !selectedPackage) {
      const popularPackage = packages.find((pkg) => pkg.isPopular)
      if (popularPackage) {
        setSelectedPackage(popularPackage)
      }
    }
  }, [packages, selectedPackage])

  // Reset student checkbox whenever the selected package changes
  useEffect(() => {
    setIsStudent(false)
  }, [selectedPackage?.id])

  // Prefill form fields on localhost for testing
  useEffect(() => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      setUserInfo({
        name: 'Eddie',
        email: 'edull165@gmail.com',
        phoneNumber: '0723125044'
      })
    }
  }, [])

  // Fetch client data for banner
  useEffect(() => {
    let isMounted = true

    const fetchClient = async () => {
      if (clientId) {
        try {
          const clientData = await getClient(clientId)
          if (isMounted && clientData) {
            setClient(clientData)
          }
        } catch (error) {
          if (isMounted) {
            console.error('Failed to fetch client data:', error)
          }
        }
      }
    }
    fetchClient()

    return () => {
      isMounted = false
    }
  }, [clientId])

  useEffect(() => {
    if (!swishPaymentIntentMonitor) return
    if (swishPaymentIntentMonitor.status == 'success') {
      // db listening is not error
      // @ts-ignore
      const { status } = swishPaymentIntentMonitor.data // get payment status data; need just the status
      if (status) {
        setSwishPaymentIntentStatus(status)
      }
    }
  }, [swishPaymentIntentMonitor.data])

  useEffect(() => {
    console.log('swish payment status', swishPaymentIntentStatus)
    if (swishPaymentIntentStatus == SwishPaymentStatus.PAID) {
      setShowSuccessModal(true)
      setIsProcessing(false)
    }
    if (swishPaymentIntentStatus == SwishPaymentStatus.ERROR) {
      setSwishInstructionUUID('') // this will allow us to come back to state where user need to enter the phone number
      setPaymentError('Got error when processing Swish payment')
      setIsProcessing(false)
    }
    if (
      [SwishPaymentStatus.CANCELLED, SwishPaymentStatus.DECLINED].includes(
        swishPaymentIntentStatus as SwishPaymentStatus
      )
    ) {
      setSwishInstructionUUID('') // this will allow us to come back to state where user need to enter the phone number
      setIsProcessing(false)
    }
  }, [swishPaymentIntentStatus])

  const handleCloseSuccessModal = async () => {
    setShowSuccessModal(false)
    if (!ticketId || !clientId) {
      setSelectedPackage(null)
      return
    }
    setPostModalLoading(true)
    try {
      const result = await getDropinTicketActivationStatus({ ticketId, clientId })
      if ('isError' in result) {
        setPostModalTicketView('qr')
        setLastConsumeLog(null)
      } else if (result.lastConsumeLog && isRecentlyActivated(result.lastConsumeLog)) {
        setPostModalTicketView('activated')
        setLastConsumeLog(result.lastConsumeLog)
      } else {
        setPostModalTicketView('qr')
        setLastConsumeLog(null)
      }
    } catch {
      setPostModalTicketView('qr')
      setLastConsumeLog(null)
    } finally {
      setPostModalLoading(false)
    }
  }

  const handleResetPostModalView = () => {
    clearStripePostPaymentFallbackTimer()
    stripePostPaymentResolvedRef.current = false
    setPostModalTicketView(null)
    setLastConsumeLog(null)
    setTicketId(null)
    setSelectedPackage(null)
    setStripePaymentIntentIdPersisted(null)
  }

  const handleRefreshActivationStatus = async () => {
    if (!ticketId || !clientId) return
    setPostModalRefreshLoading(true)
    try {
      const result = await getDropinTicketActivationStatus({ ticketId, clientId })
      if ('isError' in result) {
        setPostModalTicketView('qr')
        setLastConsumeLog(null)
      } else if (result.lastConsumeLog && isRecentlyActivated(result.lastConsumeLog)) {
        setPostModalTicketView('activated')
        setLastConsumeLog(result.lastConsumeLog)
      } else {
        setPostModalTicketView('qr')
        setLastConsumeLog(null)
      }
    } catch {
      setPostModalTicketView('qr')
      setLastConsumeLog(null)
    } finally {
      setPostModalRefreshLoading(false)
    }
  }

  // Stripe post-payment: Firestore stripe_payment_intent_status / succeeded (race with poll below)
  useEffect(() => {
    if (!postModalLoading || !stripePaymentIntentIdPersisted) return
    if (!stripePaymentIntentStatus?.data) return
    const { status } = stripePaymentIntentStatus.data as { status: StripePaymentIntentStatusValue }
    if (status === StripePaymentIntentStatusValue.Succeeded) {
      completeStripePostPaymentSuccess()
    } else if (status === StripePaymentIntentStatusValue.Failed) {
      stripePostPaymentResolvedRef.current = true
      clearStripePostPaymentFallbackTimer()
      setStripePaymentIntentIdPersisted(null)
      setPostModalLoading(false)
      setPaymentFailed(true)
    }
  }, [
    stripePaymentIntentStatus?.data,
    postModalLoading,
    stripePaymentIntentIdPersisted,
    completeStripePostPaymentSuccess,
    clearStripePostPaymentFallbackTimer
  ])

  // Stripe post-payment: ticket becomes Active in Firestore when webhook runs (may arrive before/without client PI doc)
  useEffect(() => {
    if (!postModalLoading || !ticketId || !clientId || !stripePaymentIntentIdPersisted) return

    let cancelled = false

    const tick = async () => {
      if (cancelled || stripePostPaymentResolvedRef.current) return
      try {
        const result = await getDropinTicketActivationStatus({ ticketId, clientId })
        if (cancelled || stripePostPaymentResolvedRef.current) return
        if ('isError' in result) return
        if (result.ticketStatus === DropinTicketStatus.Active) {
          completeStripePostPaymentSuccess()
        }
      } catch {
        // ignore
      }
    }

    void tick()
    const interval = setInterval(() => void tick(), 2500)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [postModalLoading, ticketId, clientId, stripePaymentIntentIdPersisted, completeStripePostPaymentSuccess])

  // Poll activation status when showing QR view (ticket not yet used)
  useEffect(() => {
    if (postModalTicketView !== 'qr' || !ticketId || !clientId) return
    const pollInterval = setInterval(async () => {
      try {
        const result = await getDropinTicketActivationStatus({ ticketId, clientId })
        if (!('isError' in result) && result.lastConsumeLog && isRecentlyActivated(result.lastConsumeLog)) {
          setPostModalTicketView('activated')
          setLastConsumeLog(result.lastConsumeLog)
        }
      } catch {
        // Ignore poll errors
      }
    }, 30000) // Poll every 30 seconds
    return () => clearInterval(pollInterval)
  }, [postModalTicketView, ticketId, clientId])

  const handleCloseSuccessPage = () => {
    setPaymentSuccess(false)
    setTicketId(null)
    setSelectedPackage(null)
    setIsStudent(false)
    setUserInfo({ name: '', phoneNumber: '', email: '' })
    setValidationErrors({})
  }

  const handleApplyReturningProfile = useCallback(() => {
    if (!returningProfile) return
    setUserInfo({
      name: returningProfile.name,
      email: returningProfile.email,
      phoneNumber: returningProfile.phone
    })
  }, [returningProfile])

  const handleDismissBanner = useCallback(() => {
    setBannerDismissed(true)
  }, [])

  const handlePaymentFailed = () => {
    setPaymentFailed(true)
    setShowSuccessModal(false)
    setSelectedPackage(null)
    setTicketId(null)
  }

  const handleOpenSwishApp = () => {
    // Try to open Swish app using deep link
    const swishUrl = 'swish://'
    const fallbackUrl = 'https://swish.nu'

    // Create a temporary link element to trigger the deep link
    const link = document.createElement('a')
    link.href = swishUrl
    link.target = '_blank'
    link.rel = 'noopener noreferrer'

    // Add to DOM, click, then remove
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Fallback: open Swish website in new tab after a short delay
    setTimeout(() => {
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
    }, 1000)
  }

  const validateEmail = (email: string): string | undefined => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!email) return getGrooverText('validationEmailRequired')
    if (!emailRegex.test(email)) return getGrooverText('validationEmailInvalid')
    return undefined
  }

  const validatePhoneNumber = (phone: string): string | undefined => {
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{8,}$/
    if (!phone) return getGrooverText('validationPhoneRequired')
    if (!phoneRegex.test(phone)) return getGrooverText('validationPhoneInvalid')
    return undefined
  }

  // Helper function to format text with {0}, {1}, etc. placeholders
  const formatTextWithPlaceholders = (text: string, params: any[]): string => {
    if (!params || params.length === 0) return text
    let formatted = text
    params.forEach((param, i) => {
      const placeholder = new RegExp(`\\{${i}\\}`, 'g')
      formatted = formatted.replace(placeholder, param?.toString() || '')
    })
    return formatted
  }

  // Helper component for dynamic texts
  const DynamicText = ({ textKey, params, styleKey }: { textKey: string; params: any[]; styleKey: string }) => {
    const text = formatTextWithPlaceholders(getGrooverText(textKey), params)
    const designSystemStyle = getGrooverTextStyle(styleKey)
    return <span style={designSystemStyle}>{text}</span>
  }

  const getPackageTitle = (pkg: DropinPackage) => {
    if (pkg.name) {
      return <span>{pkg.name}</span>
    }
    if (pkg.numberOfClips === 1) {
      return <GrooverText textKey="checkoutPageSingleTicket" />
    }
    return <DynamicText textKey="checkoutPageClipCard" params={[pkg.numberOfClips]} styleKey="h4-dark" />
  }

  const getPackageDescription = (numberOfClips: number) => {
    if (numberOfClips === 1) {
      return <GrooverText textKey="checkoutPageOneLesson" />
    }
    return <DynamicText textKey="checkoutPageLessons" params={[numberOfClips]} styleKey="small-dark" />
  }

  const handleUserInfoChange = (field: keyof UserInfo, value: string) => {
    setUserInfo((prev) => ({
      ...prev,
      [field]: value
    }))

    // Clear validation error when user starts typing
    if (validationErrors[field as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined
      }))
    }

    // Validate on change
    if (field === 'email') {
      const error = validateEmail(value)
      setValidationErrors((prev) => ({
        ...prev,
        email: error
      }))
    } else if (field === 'phoneNumber') {
      const error = validatePhoneNumber(value)
      setValidationErrors((prev) => ({
        ...prev,
        phoneNumber: error
      }))
    }
  }

  const handleCancelSwishPayment = async () => {
    setPaymentAbortInProgress(true)
    await cancelSwishPaymentIntent({ instructionUUID: swishInstructionUUID })
    setSwishInstructionUUID('')
    setSwishPaymentIntentStatus(SwishPaymentStatus.PENDING)
    setShowSuccessModal(false)
    setPaymentAbortInProgress(false)
  }

  /**
   * MUI fires `onClose` on backdrop click and Escape. Dismissing the dialog while the Swish
   * request is still open left it live at Swish for its full ~3 min validity, so the customer's
   * next attempt was rejected with RP06 (COU-333).
   */
  const handleDismissSwishModal = () => {
    if (
      swishInstructionUUID &&
      [SwishPaymentStatus.PENDING, SwishPaymentStatus.CREATED].includes(swishPaymentIntentStatus)
    ) {
      void handleCancelSwishPayment()
      return
    }
    void handleCloseSuccessModal()
  }

  const handleProcessPayment = async () => {
    if (!clientId || !selectedPackage) {
      console.error('Client ID and selected package are required')
      return
    }

    // Validate form
    const emailError = validateEmail(userInfo.email)
    const phoneError = validatePhoneNumber(userInfo.phoneNumber)

    if (!userInfo.name || emailError || phoneError) {
      setValidationErrors({
        email: emailError,
        phoneNumber: phoneError
      })
      setPaymentError(getGrooverText('validationFixErrors'))
      return
    }

    setIsProcessing(true)
    setPaymentError(null)

    try {
      const result = await createDropinTicketAndInitiateSwishPayment({
        phoneNumber: getInternationalPhoneNumber(userInfo.phoneNumber),
        name: userInfo.name,
        email: userInfo.email,
        clientId,
        packageId: selectedPackage.id,
        isStudent: isStudent && !!selectedPackage.studentDiscount
      })

      if ('isError' in result) {
        throw new Error(result.message || 'Failed to complete checkout')
      }

      setTicketId(result.ticketId)
      setSwishInstructionUUID(result.instructionUUID)
      setShowSuccessModal(true)
    } catch (error) {
      console.error('Error purchasing package:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to process payment'
      setSwishInstructionUUID('') // this will allow us to come back to state where user need to enter the phone number
      setPaymentError(errorMessage)
      setIsProcessing(false)
    }
  }

  const handleStripePayment = async () => {
    if (!clientId || !selectedPackage) return

    const emailError = validateEmail(userInfo.email)
    const phoneError = validatePhoneNumber(userInfo.phoneNumber)
    if (!userInfo.name || emailError || phoneError) {
      setValidationErrors({ email: emailError, phoneNumber: phoneError })
      setPaymentError(getGrooverText('validationFixErrors'))
      return
    }

    setIsProcessing(true)
    setPaymentError(null)

    try {
      const result = await createStripePaymentIntentForDropinTicket({
        clientId,
        packageId: selectedPackage.id,
        name: userInfo.name,
        email: userInfo.email,
        phoneNumber: userInfo.phoneNumber,
        isStudent: isStudent && !!selectedPackage.studentDiscount
      })

      if ('isError' in result) throw new Error(result.message)

      const config = await getStripeConfig(client!)
      setStripeConfig(config)
      setStripeClientSecret(result.clientSecret)
      setStripeTicketId(result.ticketId)
      setTicketId(result.ticketId)
    } catch (error) {
      setPaymentError(error instanceof Error ? error.message : 'Failed to initiate Stripe payment')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleStripeSuccess = () => {
    const paymentIntentId = stripeClientSecret?.split('_secret_')[0] ?? null
    if (paymentIntentId) {
      setStripePaymentIntentIdPersisted(paymentIntentId)
    }
    setStripeClientSecret(null)
    if (!ticketId || !clientId) {
      setSelectedPackage(null)
      return
    }
    stripePostPaymentResolvedRef.current = false
    clearStripePostPaymentFallbackTimer()
    setPostModalLoading(true)
    stripePostPaymentFallbackTimerRef.current = setTimeout(() => {
      completeStripePostPaymentSuccess()
    }, 30000)
  }

  const formatPrice = (priceInCents: number) => {
    return `${(priceInCents / 100).toFixed(0)} ${currency}`
  }

  if (loading) {
    return (
      <Box fullWidth align="center" justify="center" style={{ minHeight: '400px' }}>
        <CircularProgress size={60} thickness={4} />
        <GrooverText textKey="checkoutPageLoading" />
      </Box>
    )
  }

  if (error) {
    return (
      <Box fullWidth align="center" style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
        <Alert severity="error">Error loading packages: {error}</Alert>
      </Box>
    )
  }

  if (productType !== 'dropinPackages') {
    return (
      <Box fullWidth align="center" style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
        <Alert severity="warning">
          Product type "{productType}" is not supported. Currently only "dropinPackages" is available.
        </Alert>
      </Box>
    )
  }

  if (!packages || packages.length === 0) {
    return (
      <CheckoutContainer>
        <EmptyStateContainer>
          <EmptyStateContent>
            <EmptyStateIcon>
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="40" cy="40" r="38" stroke="#E0E0E0" strokeWidth="2" fill="#F8F9FA" />
                <path
                  d="M25 35L35 45L55 25"
                  stroke="#9E9E9E"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="40" cy="40" r="3" fill="#9E9E9E" />
              </svg>
            </EmptyStateIcon>
            <Box gap="16px" align="center">
              <GrooverText textKey="checkoutPageEmptyStateTitle" />
              <Box style={{ maxWidth: '480px' }} align="center">
                <GrooverText textKey="checkoutPageEmptyStateDescription" />
              </Box>
            </Box>
          </EmptyStateContent>
        </EmptyStateContainer>
      </CheckoutContainer>
    )
  }

  // Show success screen if payment was successful
  if (paymentSuccess) {
    return (
      <CheckoutContainer>
        {/* School Banner */}
        {client?.logoFullUrl && (
          <SchoolBanner>
            <SchoolLogo src={client.logoFullUrl} alt={client.name} />
          </SchoolBanner>
        )}

        {/* Success Content */}
        <SuccessContent>
          <SuccessIcon>✓</SuccessIcon>
          <SuccessTitle>
            <GrooverText textKey="checkoutPagePaymentSuccess" />
          </SuccessTitle>
          <SuccessMessage>
            <GrooverText textKey="checkoutPagePaymentSuccessMessage" />
          </SuccessMessage>

          {/* QR Code Section */}
          {ticketId && (
            <QRCodeSection>
              <QRCodeTitle>
                <GrooverText textKey="checkoutPageYourTicketId" />
              </QRCodeTitle>
              <QRCodeContainer>
                <QRCodeElement value={`d/${ticketId}`} />
              </QRCodeContainer>
              <TicketIdText>{ticketId}</TicketIdText>
            </QRCodeSection>
          )}

          {showAppDownloadSection && (
            <DownloadSection>
              <DownloadTitle>
                <GrooverText textKey="checkoutPageDownloadApp" />
              </DownloadTitle>
              <AppDescription>
                <GrooverText textKey="registrationStep5PaymentAppDescription" />
              </AppDescription>
              <DownloadButtonsContainer>
                <AppStoreButton
                  href="https://apps.apple.com/se/app/coursely-se/id6745610767?l=en-GB"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AppStoreBadge
                    src={_getLanguageCode() === 'sv' ? swedishAppStoreBadge : englishAppStoreBadge}
                    alt="Download on the App Store"
                  />
                </AppStoreButton>
                <GooglePlayButton
                  href="https://play.google.com/store/apps/details?id=se.coursely.app&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <GooglePlayBadge
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Get it on Google Play"
                  />
                </GooglePlayButton>
              </DownloadButtonsContainer>
            </DownloadSection>
          )}

          {/* Return to Checkout Button */}
          <ReturnButton onClick={handleCloseSuccessPage}>
            <GrooverText textKey="checkoutPageReturnToCheckout" />
          </ReturnButton>
        </SuccessContent>
      </CheckoutContainer>
    )
  }

  const dropinAppleWalletUrl =
    ticketId && clientId
      ? `${getFunctionUrl(CloudFunction.appleWalletDropinPass)}?ticketId=${encodeURIComponent(ticketId)}&clientId=${encodeURIComponent(clientId)}`
      : null
  const dropinGoogleWalletUrl =
    ticketId && clientId
      ? `${getFunctionUrl(CloudFunction.googleWalletDropinPass)}?ticketId=${encodeURIComponent(ticketId)}&clientId=${encodeURIComponent(clientId)}`
      : null

  return (
    <CheckoutContainer>
      {/* School Banner */}
      {client?.logoFullUrl && (
        <SchoolBanner>
          <SchoolLogo src={client.logoFullUrl} alt={client.name} />
        </SchoolBanner>
      )}

      {/* Payment Failed Message */}
      {paymentFailed && (
        <PaymentFailedAlert>
          <PaymentFailedIcon>⚠️</PaymentFailedIcon>
          <PaymentFailedContent>
            <PaymentFailedTitle>
              <GrooverText textKey="checkoutPagePaymentFailed" />
            </PaymentFailedTitle>
            <PaymentFailedMessage>
              <GrooverText textKey="checkoutPagePaymentFailedMessage" />{' '}
              <SupportEmailLink href="mailto:support@coursely.se">support@coursely.se</SupportEmailLink>
            </PaymentFailedMessage>
          </PaymentFailedContent>
          <CloseFailedButton onClick={() => setPaymentFailed(false)}>×</CloseFailedButton>
        </PaymentFailedAlert>
      )}

      {/* Post-Modal Ticket View (after closing success modal) */}
      {postModalLoading && (
        <Box fullWidth align="center" justify="center" style={{ minHeight: '240px', padding: 40 }} gap="16px">
          <CircularProgress size={48} />
          <GrooverText
            textKey="checkoutPageProcessingPayment"
            customerId={client?.id}
            style={{ textAlign: 'center' }}
          />
          <GrooverText
            textKey="checkoutPageConfirmingTicketMessage"
            customerId={client?.id}
            style={{ textAlign: 'center', maxWidth: 420, opacity: 0.9 }}
          />
        </Box>
      )}

      {!postModalLoading && postModalTicketView && ticketId && (
        <PostModalTicketViewContent
          postModalTicketView={postModalTicketView}
          lastConsumeLog={lastConsumeLog}
          ticketId={ticketId}
          clientId={clientId}
          onReset={handleResetPostModalView}
          onRefresh={handleRefreshActivationStatus}
          refreshLoading={postModalRefreshLoading}
        />
      )}

      {/* Main Content - 2 Column Layout (hidden while confirming payment / ticket or showing post-payment ticket view) */}
      {!postModalTicketView && !postModalLoading && (
        <MainContent>
          {/* Left Column - Package Selection */}
          <LeftColumn>
            <SectionTitle>
              <GrooverText textKey="checkoutPageChoosePackage" />
            </SectionTitle>

            <PackageList>
              {packages.map((pkg) => {
                const isSelected = selectedPackage?.id === pkg.id
                const isPopular = pkg.isPopular

                return (
                  <PackageOption key={pkg.id} $isSelected={isSelected} onClick={() => setSelectedPackage(pkg)}>
                    <PackageInfo>
                      <PackageTitle>
                        {pkg.colorCode && /^#[0-9A-Fa-f]{6}$/.test(pkg.colorCode) && (
                          <ColorSwatch style={{ backgroundColor: pkg.colorCode }} />
                        )}
                        {getPackageTitle(pkg)}
                      </PackageTitle>
                      {isPopular && (
                        <PopularTag>
                          <GrooverText textKey="checkoutPagePopular" style={{ color: 'inherit' }} />
                        </PopularTag>
                      )}
                      <PackageDescription>{getPackageDescription(pkg.numberOfClips)}</PackageDescription>
                      <PackagePrice>{formatPrice(pkg.totalPrice)}</PackagePrice>
                    </PackageInfo>
                    <RadioButton $isSelected={isSelected}>{isSelected && <CheckCircleIcon />}</RadioButton>
                  </PackageOption>
                )
              })}
            </PackageList>
          </LeftColumn>

          {/* Right Column - User Info and Payment */}
          <RightColumn>
            {/* Returning user banner */}
            {!bannerDismissed && !!returningProfile && (
              <div style={{ marginBottom: '16px' }}>
                <ReturningUserBanner
                  returningProfile={returningProfile}
                  onApply={handleApplyReturningProfile}
                  onDismiss={handleDismissBanner}
                />
              </div>
            )}

            {/* User Details Section */}
            <UserDetailsSection>
              <SectionTitle>
                <GrooverText textKey="checkoutPageYourDetails" />
              </SectionTitle>

              <FormField>
                <GrooverText textKey="checkoutPageFirstName" />
                <StyledInput
                  placeholder={getGrooverText('checkoutPageEnterName')}
                  value={userInfo.name}
                  onChange={(e) => handleUserInfoChange('name', e.target.value)}
                />
              </FormField>

              <FormField>
                <GrooverText textKey="checkoutPageEmail" />
                <StyledInput
                  placeholder={getGrooverText('checkoutPageEnterEmail')}
                  value={userInfo.email}
                  onChange={(e) => handleUserInfoChange('email', e.target.value)}
                  error={!!validationErrors.email}
                />
                {validationErrors.email && <ErrorMessage>{validationErrors.email}</ErrorMessage>}
              </FormField>

              <FormField>
                <GrooverText textKey="checkoutPagePhoneNumber" />
                <StyledInput
                  placeholder={getGrooverText('checkoutPageEnterPhone')}
                  value={userInfo.phoneNumber}
                  onChange={(e) => handleUserInfoChange('phoneNumber', e.target.value)}
                  error={!!validationErrors.phoneNumber}
                />
                {validationErrors.phoneNumber && <ErrorMessage>{validationErrors.phoneNumber}</ErrorMessage>}
              </FormField>

              {selectedPackage?.studentDiscount ? (
                <StudentDiscountCard
                  $checked={isStudent}
                  onClick={() => setIsStudent(!isStudent)}
                  role="checkbox"
                  aria-checked={isStudent}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault()
                      setIsStudent(!isStudent)
                    }
                  }}
                >
                  <StudentDiscountLeft>
                    <StudentIconWrapper $checked={isStudent}>🎓</StudentIconWrapper>
                    <StudentDiscountText>
                      <StudentDiscountTitle>
                        <GrooverText textKey="checkoutPageStudentCheckbox" />
                      </StudentDiscountTitle>
                      <StudentDiscountSavings $checked={isStudent}>
                        {formatTextWithPlaceholders(getGrooverText('checkoutPageStudentDiscountSaves'), [
                          formatPrice(selectedPackage.studentDiscount)
                        ])}
                      </StudentDiscountSavings>
                    </StudentDiscountText>
                  </StudentDiscountLeft>
                  <StudentCheckboxToggle $checked={isStudent}>
                    {isStudent && (
                      <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                        <path
                          d="M1 5L4.5 8.5L11 1"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </StudentCheckboxToggle>
                </StudentDiscountCard>
              ) : null}
            </UserDetailsSection>

            {/* Order Summary Section - Only show when package is selected */}
            {selectedPackage && (
              <OrderSummarySection>
                <SectionTitle>
                  <GrooverText textKey="checkoutPageOrderSummary" />
                </SectionTitle>

                <OrderItem>
                  <span>{getPackageTitle(selectedPackage)}</span>
                  <span>{formatPrice(selectedPackage.totalPrice)}</span>
                </OrderItem>

                {isStudent && selectedPackage.studentDiscount ? (
                  <OrderItem style={{ color: '#16a34a' }}>
                    <span>
                      <GrooverText textKey="checkoutPageStudentDiscount" />
                    </span>
                    <span>-{formatPrice(selectedPackage.studentDiscount)}</span>
                  </OrderItem>
                ) : null}

                <OrderTotal>
                  <span>
                    <GrooverText textKey="checkoutPageTotal" />
                  </span>
                  <span>
                    {formatPrice(
                      isStudent && selectedPackage.studentDiscount
                        ? Math.max(0, selectedPackage.totalPrice - selectedPackage.studentDiscount)
                        : selectedPackage.totalPrice
                    )}
                  </span>
                </OrderTotal>
              </OrderSummarySection>
            )}

            {/* Payment Section - Only show when package is selected */}
            {selectedPackage && (
              <PaymentSection>
                {/* Payment method toggle - only show when both are configured */}
                {client?.swish && client?.stripe && isPaymentMethodEnabled(PaymentMethod.StripeDropin, client) && (
                  <PaymentMethodToggle>
                    <PaymentMethodButton $active={paymentMethod === 'swish'} onClick={() => setPaymentMethod('swish')}>
                      <img src={swishIcon} width="20px" height="20px" alt="Swish" />
                      Swish
                    </PaymentMethodButton>
                    <PaymentMethodButton
                      $active={paymentMethod === 'stripe'}
                      onClick={() => setPaymentMethod('stripe')}
                    >
                      💳 Card
                    </PaymentMethodButton>
                  </PaymentMethodToggle>
                )}

                {/* Stripe Elements form */}
                {paymentMethod === 'stripe' && stripeClientSecret && stripeConfig && (
                  <Elements stripe={getStripe(stripeConfig)} options={{ clientSecret: stripeClientSecret }}>
                    <StripeCheckoutForm onIsPaid={handleStripeSuccess} />
                  </Elements>
                )}

                {/* Swish pay button */}
                {(paymentMethod === 'swish' ||
                  !(client?.stripe && isPaymentMethodEnabled(PaymentMethod.StripeDropin, client))) &&
                  !stripeClientSecret && (
                    <PayButton onClick={handleProcessPayment} disabled={!selectedPackage || isProcessing}>
                      {isProcessing ? (
                        <>
                          <CircularProgress size={20} color="inherit" />
                          <GrooverText textKey="checkoutPageProcessing" />
                        </>
                      ) : (
                        <>
                          <SwishIcon>
                            <img src={swishIcon} width="20px" height="20px" alt="Swish" />
                          </SwishIcon>
                          {formatTextWithPlaceholders(getGrooverText('checkoutPagePayWithSwishButton'), [
                            formatPrice(
                              isStudent && selectedPackage.studentDiscount
                                ? Math.max(0, selectedPackage.totalPrice - selectedPackage.studentDiscount)
                                : selectedPackage.totalPrice
                            )
                          ])}
                        </>
                      )}
                    </PayButton>
                  )}

                {/* Stripe initiate button */}
                {paymentMethod === 'stripe' &&
                  client?.stripe &&
                  isPaymentMethodEnabled(PaymentMethod.StripeDropin, client) &&
                  !stripeClientSecret && (
                    <PayButton onClick={handleStripePayment} disabled={!selectedPackage || isProcessing}>
                      {isProcessing ? (
                        <>
                          <CircularProgress size={20} color="inherit" />
                          <GrooverText textKey="checkoutPageProcessing" />
                        </>
                      ) : (
                        <>
                          💳{' '}
                          {formatTextWithPlaceholders(getGrooverText('checkoutPagePayWithCardButton'), [
                            formatPrice(
                              isStudent && selectedPackage.studentDiscount
                                ? Math.max(0, selectedPackage.totalPrice - selectedPackage.studentDiscount)
                                : selectedPackage.totalPrice
                            )
                          ])}
                        </>
                      )}
                    </PayButton>
                  )}

                {paymentMethod !== 'stripe' && (
                  <PaymentInfo>
                    <GrooverText textKey="checkoutPageSwishRedirectInfo" />
                  </PaymentInfo>
                )}
              </PaymentSection>
            )}
          </RightColumn>
        </MainContent>
      )}

      {/* Payment Error Display */}
      {paymentError && (
        <ErrorAlert>
          <Alert severity="error" onClose={() => setPaymentError(null)}>
            {paymentError}
          </Alert>
        </ErrorAlert>
      )}

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={handleDismissSwishModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          {/* Swish Logo */}
          <SwishLogoContainer>
            <SwishLogoImage src={swishBigLogo} alt="Swish" />
          </SwishLogoContainer>

          {[SwishPaymentStatus.PENDING, SwishPaymentStatus.CREATED].includes(swishPaymentIntentStatus) && (
            <>
              {/* Title */}
              <SwishModalTitle>
                <GrooverText textKey="checkoutPageOpenSwishTitle" />
              </SwishModalTitle>
              {/* Description */}
              <SwishModalDescription>
                <GrooverText textKey="checkoutPageOpenSwishDescription" />
              </SwishModalDescription>
            </>
          )}

          {/* Open Swish Button - Only show on phones */}
          {isPhoneDevice && (
            <OpenSwishButton onClick={handleOpenSwishApp}>
              <GrooverText textKey="checkoutPageOpenSwishButton" style={{ color: 'white' }} />
            </OpenSwishButton>
          )}

          {/* Failed Payment Button - Only show on localhost for testing */}
          {paymentFailed && (
            <FailedPaymentButton onClick={handlePaymentFailed}>
              <GrooverText textKey="checkoutPageFailedPaymentButton" />
            </FailedPaymentButton>
          )}

          {swishPaymentIntentStatus === SwishPaymentStatus.PAID && (
            <PaymentSuccessContent>
              <SuccessIcon>
                <CheckCircleIcon sx={{ fontSize: 40 }} />
              </SuccessIcon>
              <SuccessTitle>
                <GrooverText textKey="checkoutPagePaymentSuccessTitle" />
              </SuccessTitle>
              <SuccessMessage>
                <GrooverText textKey="checkoutPagePaymentSuccessMessage" />
              </SuccessMessage>
              <PortalInfoText>
                <GrooverText textKey="checkoutPageSeeTicketsInPortalPrefix" />
                <PortalLink to={buildRoute(RoutePath.PORTAL)}>
                  <GrooverText textKey="checkoutPageSeeTicketsInPortalLink" />
                </PortalLink>
              </PortalInfoText>
              {ticketId && (
                <ModalQRCodeSection>
                  <QRCodeContainer>
                    <QRCodeElement value={`d/${ticketId}`} />
                  </QRCodeContainer>
                  <AddToWalletButtons appleWalletUrl={dropinAppleWalletUrl} googleWalletUrl={dropinGoogleWalletUrl} />
                </ModalQRCodeSection>
              )}
            </PaymentSuccessContent>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          {[SwishPaymentStatus.PENDING, SwishPaymentStatus.CREATED].includes(swishPaymentIntentStatus) && (
            <>
              <CloseButton
                onClick={handleCancelSwishPayment}
                style={{ width: '100%' }}
                disabled={paymentAbortInProgress}
              >
                {paymentAbortInProgress && <CircularProgress size={20} color="inherit" />}
                <GrooverText textKey={Texts.registrationCtactaAbort} />
              </CloseButton>
              <CircularProgress size={20} color="inherit" />
            </>
          )}
          {swishPaymentIntentStatus === SwishPaymentStatus.PAID && (
            <CloseButton onClick={handleCloseSuccessModal} style={{ width: '100%' }}>
              <GrooverText textKey="checkoutPageClose" style={{ color: 'white' }} />
            </CloseButton>
          )}
        </DialogActions>
      </Dialog>
    </CheckoutContainer>
  )
}

// New styled components for the redesigned checkout page
const CheckoutContainer = styled.div`
  min-height: 100vh;
  background-color: white;
`

const SchoolBanner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
`

const SchoolLogo = styled.img`
  max-height: 80px;
  max-width: 300px;
  object-fit: contain;
`

const MainContent = styled.div`
  display: flex;
  max-width: 1400px;
  min-width: 800px;
  margin: 0 auto;
  padding: 32px 20px;
  gap: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
    gap: 24px;
    min-width: unset;
  }
`

const LeftColumn = styled.div`
  flex: 1;
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const RightColumn = styled.div`
  flex: 1;
  background: #f8f4ff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const SectionTitle = styled.div`
  margin: 0 0 24px 0;
  font-size: 18px;
  font-weight: 600;
  color: rgba(39, 42, 48, 1);
`

const PackageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PackageOption = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border: 2px solid ${(props) => (props.$isSelected ? '#007bff' : '#e0e0e0')};
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: ${(props) => (props.$isSelected ? '#007bff' : '#ccc')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`

const PackageInfo = styled.div`
  flex: 1;
`

const PackageTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #000;
  display: flex;
  align-items: center;
  gap: 8px;
`

const ColorSwatch = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
`

const PopularTag = styled.span`
  display: inline-block;
  background: #f3e8ff;
  color: #7c3aed;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid #e9d5ff;
`

const PackageDescription = styled.p`
  margin: 0 0 8px 0;
  color: #000;
  font-size: 0.9rem;
`

const PackagePrice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #000;
`

const RadioButton = styled.div<{ $isSelected: boolean }>`
  width: 24px;
  height: 24px;
  border: 2px solid ${(props) => (props.$isSelected ? '#007bff' : '#ccc')};
  border-radius: 50%;
  background: ${(props) => (props.$isSelected ? '#007bff' : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
`

const UserDetailsSection = styled.div`
  margin-bottom: 32px;
`

const FormField = styled.div`
  margin-bottom: 20px;
  width: 100%;
  box-sizing: border-box;

  > div:first-child {
    margin-bottom: 8px;
    display: block;
  }
`

const StudentDiscountCard = styled.div<{ $checked: boolean }>`
  margin-bottom: 20px;
  padding: 14px 16px;
  background: ${({ $checked }) => ($checked ? '#f0fdf4' : '#f8faff')};
  border: 2px solid ${({ $checked }) => ($checked ? '#16a34a' : '#dde8ff')};
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  transition:
    border-color 0.2s ease,
    background 0.2s ease;
  user-select: none;

  &:hover {
    border-color: ${({ $checked }) => ($checked ? '#16a34a' : '#a5c0ff')};
    background: ${({ $checked }) => ($checked ? '#f0fdf4' : '#f0f5ff')};
  }
`

const StudentDiscountLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`

const StudentIconWrapper = styled.div<{ $checked: boolean }>`
  font-size: 1.5rem;
  line-height: 1;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ $checked }) => ($checked ? '#dcfce7' : '#eff4ff')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s ease;
`

const StudentDiscountText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StudentDiscountTitle = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a2e;
`

const StudentDiscountSavings = styled.div<{ $checked: boolean }>`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ $checked }) => ($checked ? '#16a34a' : '#007bff')};
  transition: color 0.2s ease;
`

const StudentCheckboxToggle = styled.div<{ $checked: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid ${({ $checked }) => ($checked ? '#16a34a' : '#c0d0f0')};
  background: ${({ $checked }) => ($checked ? '#16a34a' : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
`

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`

const StyledInput = styled.input<{ error?: boolean }>`
  width: 100%;
  max-width: 100%;
  padding: 12px 16px;
  border: 1px solid ${(props) => (props.error ? '#d32f2f' : '#ddd')};
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.error ? '#d32f2f' : '#007bff')};
  }

  &::placeholder {
    color: #666;
  }
`

const OrderSummarySection = styled.div`
  margin-bottom: 32px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
`

const OrderTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0 0 0;
  font-weight: 600;
  font-size: 1.1rem;
`

const PaymentSection = styled.div`
  text-align: center;
`

const SwishIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`

const PayButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;

  &:hover:not(:disabled) {
    background: #0056b3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`

const PaymentInfo = styled.p`
  margin: 0;
  color: #000;
  font-size: 0.9rem;
`

const ErrorAlert = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  max-width: 500px;
  width: 90%;
`

const CloseButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: linear-gradient(45deg, #388e3c, #689f38);
    transform: translateY(-1px);
  }

  &:disabled {
    background: #cccccc;
    color: #666666;
    cursor: not-allowed;
    transform: none;
  }
`

const SuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  padding: 40px 20px;
`

const QRCodeSection = styled.div`
  margin: 32px 0;
  text-align: center;
`

const QRCodeTitle = styled.div`
  margin-bottom: 16px;
`

const QRCodeContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const TicketIdText = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #666;
  word-break: break-all;
  max-width: 300px;
  margin: 0 auto;
`

const PostModalTicketCard = styled.div`
  max-width: 420px;
  margin: 24px auto;
  padding: 32px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  text-align: center;
`

const PostModalTicketCardActivated = styled(PostModalTicketCard)`
  background: #dcfce7;
  border: 3px solid #16a34a;
`

const PostModalActivatedSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 20px;
  margin-bottom: 20px;
  background: rgba(22, 163, 74, 0.1);
  border-radius: 12px;
  border: 2px solid #16a34a;
`

const PostModalActivatedText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #166534;
  text-align: center;
`

const PostModalOkLabel = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #16a34a;
  letter-spacing: 0.05em;
`

const DownloadSection = styled.div`
  margin: 32px 0;
  text-align: center;
  max-width: 500px;
`

const DownloadTitle = styled.div`
  margin-bottom: 16px;
`

const AppDescription = styled.div`
  margin-bottom: 24px;
  line-height: 1.6;
`

const DownloadButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    gap: 20px;
  }
`

const AppStoreButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  text-decoration: none;
  transition: opacity 0.2s ease;
  border: none;
  background: none;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }
`

const AppStoreBadge = styled.img`
  width: 180px;
  height: 60px;
`

const GooglePlayButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  text-decoration: none;
  transition: opacity 0.2s ease;
  border: none;
  background: none;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }
`

const GooglePlayBadge = styled.img`
  width: 180px;
  height: 60px;
`

const ReturnButton = styled.button`
  margin-top: 32px;
  padding: 12px 24px;
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 200px;

  &:hover {
    background: linear-gradient(45deg, #388e3c, #689f38);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const RefreshButton = styled.button`
  margin-top: 16px;
  padding: 10px 20px;
  background: transparent;
  color: #4caf50;
  border: 2px solid #4caf50;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #f0fdf4;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`

const SwishLogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 24px;
`

const SwishLogoImage = styled.img`
  width: 120px;
  height: auto;
  display: block;
  margin: 0 auto;
`

const SwishModalTitle = styled.div`
  margin-bottom: 16px;
`

const SwishModalDescription = styled.div`
  margin-bottom: 24px;
  line-height: 1.6;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`

const OpenSwishButton = styled.button`
  padding: 16px 32px;
  background: #2c3e50;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;
  min-width: 200px;

  &:hover {
    background: #34495e;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const PaymentFailedAlert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #ffebee;
  border: 1px solid #f44336;
  border-radius: 8px;
  padding: 16px;
  margin: 16px;
  position: relative;
`

const PaymentFailedIcon = styled.div`
  font-size: 24px;
  flex-shrink: 0;
  margin-top: 2px;
`

const PaymentFailedContent = styled.div`
  flex: 1;
`

const PaymentFailedTitle = styled.div`
  margin-bottom: 8px;
  font-weight: 600;
`

const PaymentFailedMessage = styled.div`
  line-height: 1.5;
`

const SupportEmailLink = styled.a`
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
    color: #1565c0;
  }

  &:active {
    color: #0d47a1;
  }
`

const CloseFailedButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }
`

const FailedPaymentButton = styled.button`
  padding: 12px 24px;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 16px;

  &:hover {
    background: #d32f2f;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const EmptyStateContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 40px 20px;
`

const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 600px;
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 60px 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  color: #333;

  @media (max-width: 768px) {
    padding: 40px 24px;
    margin: 20px;
  }
`

const EmptyStateIcon = styled.div`
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PaymentSuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 0;
  gap: 16px;
`

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.3);
  animation: successPulse 0.6s ease-in-out;

  @keyframes successPulse {
    0% {
      transform: scale(0.8);
      opacity: 0;
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`

const SuccessTitle = styled.div`
  margin-bottom: 8px;
`

const SuccessMessage = styled.div`
  max-width: 400px;
  line-height: 1.5;
  opacity: 0.9;
`

const PortalInfoText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: rgba(39, 42, 48, 0.9);
`

const PortalLink = styled(Link)`
  color: #0066cc;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-weight: 600;
  &,
  & * {
    color: inherit;
  }
  &:hover {
    color: #004499;
  }
`

const ModalQRCodeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 8px;
`

const PaymentMethodToggle = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`

const PaymentMethodButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 10px;
  border: 2px solid ${(props) => (props.$active ? '#007bff' : '#e0e0e0')};
  border-radius: 8px;
  background: ${(props) => (props.$active ? '#e8f0fe' : 'white')};
  font-weight: ${(props) => (props.$active ? '600' : '400')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;
`

export default CheckoutPage
